import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Globe,
  Inbox,
  ListTodo,
  Loader2,
  Plus,
  Sun,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  completeTodo,
  deleteTodo,
  linkTodoTicket,
  listTodos,
  reopenTodo,
  unlinkAllTodoTickets,
  unlinkTodoTicket,
  type Todo,
  type TodoList,
} from "@/lib/todos";
import { markFollowUpNotificationsRead } from "@/lib/notifications";
import { FollowUpDialog } from "@/components/todos/FollowUpDialog";
import { TodoRow } from "@/components/todos/TodoRow";
import { Feature, Permission, useSession } from "@/components/session-provider";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/todos")({
  component: TodosPage,
});

/**
 * The follow-up list (D-59, BL-007).
 *
 * ⚠️ **Everything on this screen belongs to the signed-in person and nobody else can reach
 * it** — not their team lead, not an administrator, not the Owner. Enforced twice server-side
 * (the `TodoItems` query filter and `security.TodoAccessPolicy`), so this page holds no
 * ownership logic at all and there is no user id in any request it makes.
 *
 * ⚠️ **Organised by WHEN, never by status columns.** A service desk fails its users by going
 * quiet, so the question this screen answers is "what did I promise, and when" — which makes
 * overdue the first thing on it, in red, always. A board with To Do / Doing / Done columns
 * answers a different question that nobody at a desk is asking.
 *
 * ⚠️ **The buckets are the server's, and the counts on this screen are read off them rather
 * than recomputed.** "Today" is a calendar day in a place, cut in the reader's zone (D-72's
 * reasoning), and a second definition in the browser is how a heading comes to disagree with
 * the rows under it.
 *
 * ⚠️ **Nothing is assigned to anybody.** There is no assignee control here and no endpoint
 * behind one. Work that needs another person is a request or an escalation, both of which are
 * visible and audited; a task pushed silently onto a colleague's private list is neither.
 */
function TodosPage() {
  const { can, hasFeature } = useSession();

  const [list, setList] = useState<TodoList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Todo | undefined>(undefined);

  // The same permission the server gates the endpoints on. Rendering only — a browser that
  // edits its permission list gets a screen whose every call returns 404.
  const mayUse = can(Permission.TicketUpdate);
  const featureOff = !hasFeature(Feature.TodoFollowUps);

  const load = useCallback(async () => {
    try {
      setList(await listTodos());
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Your follow-ups could not be loaded just now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!mayUse || featureOff) {
      setLoading(false);
      return;
    }

    void load();

    // ⚠️ OPENING THE LIST CLEARS ITS UNREAD REMINDERS (D-95, D-70's rule). A follow-up reminder
    // names no request, so nothing else in the product can ever clear one — without this the
    // badge outlives the reader arriving at the screen the badge sent them to, which is how
    // people learn to ignore a bell.
    //
    // ⚠️ Swallowed on failure, deliberately: this is housekeeping on a page that has already
    // loaded, and the worst case is the badge clearing on the next visit instead of this one.
    void markFollowUpNotificationsRead().catch(() => {});
  }, [load, mayUse, featureOff]);

  /**
   * Applies a change and re-reads the list.
   *
   * ⚠️ Re-reads rather than moving the row in local state. Completing an item changes which
   * bucket it belongs in, and those buckets are cut server-side against the reader's own
   * timezone — a browser that re-sorted them here would be the second definition of "today"
   * this feature deliberately does not have.
   */
  async function run(todo: Todo, action: () => Promise<unknown>) {
    setBusyId(todo.id);
    setError(null);

    try {
      await action();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That change could not be saved.");
    } finally {
      setBusyId(null);
    }
  }

  const openCount = useMemo(
    () =>
      list
        ? list.overdue.length + list.today.length + list.upcoming.length + list.someday.length
        : 0,
    [list],
  );

  // The route is reachable by URL whatever the navigation shows, so both gates are answered
  // here too. Neither is the control — the endpoints refuse regardless — but a screen full
  // of failing calls reads as a broken product rather than as a feature somebody lacks.
  if (featureOff) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center md:px-6">
        <ListTodo className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
        <h1 className="mt-4 text-lg font-semibold">Follow-ups</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {/* Off, not missing — and said in those words, because "coming soon" would be
              wrong and an error would send somebody to the service desk about it. Existing
              items are untouched and come back if it is switched on again. */}
          This has been switched off for your organisation. Anything you had saved is still there.
        </p>
      </div>
    );
  }

  if (!mayUse) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center md:px-6">
        <ListTodo className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
        <h1 className="mt-4 text-lg font-semibold">Follow-ups</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This is for the people who work requests. Your own requests are under Tickets.
        </p>
      </div>
    );
  }

  const rowProps = (todo: Todo) => ({
    todo,
    busy: busyId === todo.id,
    onToggle: (t: Todo) =>
      void run(t, () => (t.completedAtUtc ? reopenTodo(t.id) : completeTodo(t.id))),
    onEdit: (t: Todo) => {
      setEditing(t);
      setDialogOpen(true);
    },
    onDelete: (t: Todo) => void run(t, () => deleteTodo(t.id)),
    onUnlink: (t: Todo, ticketNumber: string) =>
      void run(t, () => unlinkTodoTicket(t.id, ticketNumber)),
    onUnlinkAll: (t: Todo) => void run(t, () => unlinkAllTodoTickets(t.id)),

    // ⚠️ The number as typed. `run` surfaces the server's message, which is the same 404 for
    // a request that does not exist and one this reader cannot see — deliberately, because
    // telling them apart would make a private list an oracle.
    onLink: (t: Todo, ticketNumber: string) =>
      void run(t, () => linkTodoTicket(t.id, ticketNumber)),
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-primary text-primary-foreground shadow-glow"
            aria-hidden
          >
            <ListTodo className="h-5 w-5" />
          </span>

          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">Follow-ups</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {/* ⚠️ Said plainly at the top of the screen, not buried in a help page. People
                  write differently in a place they believe is private, and this one is
                  private from colleagues without being outside the system's rules. */}
              Yours alone — nobody else can see these. Kept and audited like any other record here.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-gradient-primary px-4 text-sm font-medium text-primary-foreground shadow-glow transition hover:opacity-90"
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add follow-up
        </button>
      </header>

      {/* A summary of the same buckets drawn below, read off the server's own split rather
          than counted here. It exists so somebody arriving from the bell knows what they are
          walking into before the first row has been read — and because "3 late" at the top is
          the sentence this feature is for. */}
      {!loading && list && openCount > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat
            label="Overdue"
            value={list.overdue.length}
            icon={AlertTriangle}
            tone="destructive"
          />
          <Stat label="Today" value={list.today.length} icon={Sun} tone="primary" />
          <Stat label="Coming up" value={list.upcoming.length} icon={CalendarDays} />
          <Stat label="No date" value={list.someday.length} icon={Inbox} />
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      {loading && (
        <p className="mt-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading your follow-ups…
        </p>
      )}

      {!loading && list && openCount === 0 && (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-card/40 px-6 py-14 text-center">
          <span
            className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-success/10 text-success"
            aria-hidden
          >
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <p className="mt-4 text-sm font-medium">Nothing outstanding.</p>
          <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
            Add one from here, or from the panel on any request when you promise somebody you'll
            come back to them.
          </p>
        </div>
      )}

      {!loading && list && openCount > 0 && (
        <div className="mt-7 space-y-7">
          {/* Overdue first and unmissable. A follow-up list whose late items are anywhere but
              the top has failed at the one job it has. */}
          <Bucket
            title="Overdue"
            icon={AlertTriangle}
            items={list.overdue}
            tone="destructive"
            rowProps={rowProps}
          />
          <Bucket title="Today" icon={Sun} items={list.today} tone="primary" rowProps={rowProps} />
          <Bucket title="Coming up" icon={CalendarDays} items={list.upcoming} rowProps={rowProps} />
          <Bucket title="No date" icon={Inbox} items={list.someday} rowProps={rowProps} />
        </div>
      )}

      {!loading && list && list.completed.length > 0 && (
        <div className="mt-8 border-t border-border pt-5">
          <button
            type="button"
            onClick={() => setShowCompleted((v) => !v)}
            aria-expanded={showCompleted}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <ChevronDown
              className={cn("h-3.5 w-3.5 transition-transform", showCompleted && "rotate-180")}
              aria-hidden
            />
            {showCompleted ? "Hide" : "Show"} recently done
            <span className="tabular-nums opacity-70">({list.completed.length})</span>
          </button>

          {showCompleted && (
            <ul className="mt-3 space-y-2">
              {list.completed.map((todo) => (
                <TodoRow key={todo.id} {...rowProps(todo)} />
              ))}
            </ul>
          )}
        </div>
      )}

      {!loading && list && (
        // The zone the buckets were cut in, stated rather than assumed. Somebody who has
        // travelled, or whose site is recorded elsewhere, should be able to see why "today"
        // ends when it does instead of concluding the list is wrong.
        <p className="mt-8 flex items-center gap-1.5 border-t border-border pt-4 text-[11px] text-muted-foreground">
          <Globe className="h-3 w-3 shrink-0" aria-hidden />
          Dates shown in {list.timeZoneId.replace(/_/g, " ")}.
        </p>
      )}

      <FollowUpDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(undefined);
        }}
        onSaved={() => void load()}
      />
    </div>
  );
}

/**
 * One bucket's count, at the top of the screen.
 *
 * ⚠️ Read off the same arrays the buckets below render, so the tile and the heading can never
 * disagree — the defect D-70 fixed on the home screen, where a tile said three and opened a
 * list of eleven.
 */
function Stat({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  tone?: "destructive" | "primary";
}) {
  const lit = value > 0;

  return (
    <div
      className={cn(
        "rounded-xl border px-3 py-2.5 transition",
        lit && tone === "destructive"
          ? "border-destructive/25 bg-destructive/[0.06]"
          : lit && tone === "primary"
            ? "border-primary/25 bg-primary/[0.06]"
            : "border-border bg-card",
      )}
    >
      <span
        className={cn(
          "flex items-center gap-1.5 text-[11px] font-medium tracking-wide uppercase",
          lit && tone === "destructive"
            ? "text-destructive"
            : lit && tone === "primary"
              ? "text-primary"
              : "text-muted-foreground",
        )}
      >
        <Icon className="h-3 w-3 shrink-0" />
        {label}
      </span>

      <span
        className={cn(
          "mt-0.5 block text-2xl font-semibold tabular-nums",
          lit ? "text-foreground" : "text-muted-foreground/50",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Bucket({
  title,
  icon: Icon,
  items,
  tone,
  rowProps,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: Todo[];
  tone?: "destructive" | "primary";
  rowProps: (todo: Todo) => React.ComponentProps<typeof TodoRow>;
}) {
  // Absent rather than empty. Four permanently-visible headings with nothing under three of
  // them make a list of two items look like a system somebody has fallen behind on.
  if (items.length === 0) return null;

  return (
    <section aria-labelledby={`bucket-${title}`} className="group/card">
      <div className="flex items-center gap-2">
        <h2
          id={`bucket-${title}`}
          className={cn(
            "flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase",
            tone === "destructive"
              ? "text-destructive"
              : tone === "primary"
                ? "text-primary"
                : "text-muted-foreground",
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {title}
        </h2>
        <InfoTip id={`todos-${title.toLowerCase().replace(/\s+/g, "-")}`} className="-ml-1" />

        <span
          className={cn(
            "rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
            tone === "destructive"
              ? "bg-destructive/10 text-destructive"
              : tone === "primary"
                ? "bg-primary/10 text-primary"
                : "bg-secondary text-muted-foreground",
          )}
        >
          {items.length}
        </span>

        {/* A hairline out to the edge, so a heading reads as the top of a group rather than as
            a stray label sitting above some cards. */}
        <span className="h-px flex-1 bg-border" aria-hidden />
      </div>

      <ul className="mt-2.5 space-y-2">
        {items.map((todo) => (
          <TodoRow key={todo.id} {...rowProps(todo)} />
        ))}
      </ul>
    </section>
  );
}
